import { VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import { useStoreTransaction } from "hooks/transactions";
import { useState } from "react";
import moment from "moment";
import {
  Button,
  CalendarInput,
  DropFileInput,
  FullscreenModal,
  Input,
  LargeSwitch,
  Warning,
} from "shared";
import { CompanyMyType } from "types/CompanyTypes";
import { TransactionStoreType } from "types/TransactionsTypes";
import { getCurrencyFromUuid } from "utils";
import * as Yup from "yup";
import { useGetSharesSummary } from "hooks/shares";

interface SellSharesModalProps {
  isOpen: boolean;
  onClose: () => void;
  hardClose: () => void;
  onSuccess?: (data?: any) => void;
  company: CompanyMyType | null;
  setDetailsCompany?: (company: any) => void;
  detailsCompany?: any;
}

const SellSharesModal: React.FC<SellSharesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  company,
  setDetailsCompany,
  detailsCompany,
  hardClose,
}) => {
  const { mutateAsync: storeTransaction } = useStoreTransaction();
  const { refetch } = useGetSharesSummary();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isStock = company?.type === "stock";

  const initialValues = {
    shareId: company?.shareId || "",
    sharesCount: null as number | null,
    pricePerShare: company?.pricePerShare || null,
    sharesValue: null as number | null,
    invoice: null,
    completedAt: moment().format("YYYY-MM-DD"),
    type: "sell",
  };

  const truncateTo2 = (n: number) => Math.floor(n * 100) / 100;

  const onSubmit = async (values: TransactionStoreType) => {
    setIsSubmitting(true);
    try {
      const raw =
        values.sharesValue && values.sharesCount
          ? values.sharesValue / values.sharesCount
          : 0;

      const summary =
        Number.isFinite(raw) && !Number.isNaN(raw) ? truncateTo2(raw) : 0;

      if (company) {
        await storeTransaction(
          {
            ...values,
            shareId: company?.shareId,
            pricePerShare: summary || undefined,
          },
          {
            onSuccess: (data) => {
              if (data.availableShares > 0 && setDetailsCompany) {
                setDetailsCompany({
                  type: "",
                  company: {
                    ...detailsCompany.company,
                    availableShares: data?.availableShares,
                    sharesCount: data?.availableShares,
                  },
                });
                onClose();
              } else {
                hardClose();
              }
              onSuccess?.(data);
              setTimeout(() => {
                refetch();
              }, 200);
            },
          },
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const validation = () =>
    Yup.object().shape({
      sharesCount: Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return null;
          const casted =
            typeof originalValue === "string" ? Number(originalValue) : value;
          return Number.isNaN(casted) ? null : casted;
        })
        .required(VALIDATION_ENUM.REQUIRED)
        .when([], {
          is: () => company?.type === "stock",
          then: (schema) => schema.min(1, VALIDATION_ENUM.MIN_1),
          otherwise: (schema) => schema,
        })
        .test(
          "count-integer-if-stock",
          "Ilość akcji musi być liczbą całkowitą",
          (value) => {
            if (company?.type !== "stock") return true;
            return value == null || Number.isInteger(value);
          },
        ),
      completedAt: Yup.string().required(VALIDATION_ENUM.REQUIRED),
    });

  const isBuyAvailable = company?.isNonReliancePurchase;

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <h2 className="fullscreen-modal__header">
        {`Zaznacz sprzedaż ${isBuyAvailable ? " / kupno" : ""}`}
      </h2>
      <Formik
        initialValues={initialValues}
        onSubmit={(values: any) => onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
      >
        {({ errors, values, handleSubmit, setFieldValue }) => {
          const summaryRaw =
            !!values?.sharesValue &&
            !!values?.sharesCount &&
            values?.sharesValue > 0 &&
            values?.sharesCount > 0
              ? values?.sharesValue / values?.sharesCount
              : 0;

          const summary =
            Number.isFinite(summaryRaw) && !Number.isNaN(summaryRaw)
              ? truncateTo2(summaryRaw)
              : 0;

          const isSell = values.type === "sell";
          const isMoreThanAvailable =
            isSell &&
            company?.sharesCount &&
            values?.sharesCount &&
            values?.sharesCount > company?.sharesCount;

          return (
            <Form className="m-width-100 fullscreen-modal__select-margin">
              {isBuyAvailable && (
                <LargeSwitch
                  state={values.type}
                  className="add-company__switch"
                  options={[
                    { label: "Sprzedaż", value: "sell" },
                    { label: "Kupno", value: "buy" },
                  ]}
                  onChange={(value: string) => setFieldValue("type", value)}
                />
              )}
              <Field
                type="number"
                id="sharesCount"
                name="sharesCount"
                precision={isStock ? 0 : 2}
                inputMode={isStock ? "numeric" : "decimal"}
                step={isStock ? 1 : "any"}
                label={`Ilość ${isSell ? "sprzedanych" : "kupionych"} ${
                  isStock ? "akcji" : "udziałów"
                }`}
                as={Input}
              />
              {isSell && (
                <Warning
                  className={isMoreThanAvailable ? "" : "warning--gray"}
                  text={`Nie możesz sprzedać więcej udziałów niż posiadasz. Obecnie posiadasz ${company?.sharesCount}.`}
                />
              )}
              <div className="input--currency">
                <Field
                  type="number"
                  id="sharesValue"
                  name="sharesValue"
                  maxLength={10}
                  optional
                  precision={2}
                  label={`Wartość ${isSell ? "sprzedanych" : "kupionych"} ${
                    isStock ? "akcji" : "udziałów"
                  }`}
                  as={Input}
                />
                <span>
                  {company?.currencyUuid
                    ? getCurrencyFromUuid(company?.currencyUuid)
                    : null}
                </span>
              </div>
              <div className={`add-share__summary`}>
                <label className="input__label">
                  Wartość {isStock ? "jednej akcji" : "jednego udziału"}
                </label>
                <input
                  type="text"
                  className="input"
                  readOnly
                  value={summary || 0}
                />
                <span>
                  {company?.currencyUuid &&
                    getCurrencyFromUuid(company?.currencyUuid)}
                </span>
              </div>
              <Field
                type="date"
                id="completedAt"
                name="completedAt"
                label={"Data transakcji"}
                component={CalendarInput}
                errors={errors}
              />

              <Field
                label="Umowa"
                name="invoice"
                optional
                id="invoice"
                as={DropFileInput}
                accept=".pdf"
              />

              <div className="fullscreen-modal__buttons">
                <Button
                  label="Anuluj"
                  onClick={onClose}
                  className="button--stroke button--rounded"
                />
                <Button
                  label={"Potwierdź"}
                  type="submit"
                  onClick={() => null}
                  disabled={!!isMoreThanAvailable}
                  className=" button--rounded button--black"
                  isLoading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </FullscreenModal>
  );
};

export default SellSharesModal;
